/**
 * GlobalView — Satellite Detail Popup
 *
 * Overlay showing satellite details: name, NORAD ID, orbit params,
 * position, altitude classification. Draggable via header.
 * Reuses the AircraftPopup CSS module for consistent styling.
 *
 * Features:
 * - "MEHR INFO" button to load Wikipedia satellite info
 * - Expandable panel with photo, summary, and article link
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import { stopSatelliteTracking } from '../../services/satelliteTracker';
import { fetchSatelliteInfo } from '../../services/satelliteInfoService';
import EntityBottomSheet from './EntityBottomSheet';
import type { SatelliteWikiInfo } from '../../services/satelliteInfoService';
import type { SatelliteProperties } from '@globalview/shared';
import styles from './AircraftPopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function fmtCoord(val: number, decimals = 4): string {
  return val.toFixed(decimals);
}

function fmtAltKm(altMeters: number): string {
  return `${(altMeters / 1000).toFixed(1)} km`;
}

function altClass(altKm: number): string {
  if (altKm < 2_000) return 'LEO';
  if (altKm < 35_786) return 'MEO';
  if (altKm < 36_000) return 'GEO';
  return 'HEO';
}

function fmtPeriod(minutes: number): string {
  if (!minutes || minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}min` : `${m}min`;
}

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 5) return 'jetzt';
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  return `vor ${Math.round(diff / 60)}min`;
}

export default function SatellitePopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const trackedEntityId = useGlobeStore((s) => s.trackedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  // Wiki state
  const [wikiInfo, setWikiInfo] = useState<SatelliteWikiInfo | null>(null);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [wikiError, setWikiError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      setWikiInfo(null);
      setWikiLoading(false);
      setWikiError(false);
      setExpanded(false);
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  useEffect(() => {
    if (!selectedEntityId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setSelectedEntity(null); stopSatelliteTracking(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  const handleMoreInfo = useCallback(async () => {
    if (!entity || wikiLoading) return;
    const props = entity.properties as unknown as SatelliteProperties;
    const name = props?.name ?? entity.id;
    setWikiLoading(true);
    setWikiError(false);
    try {
      const info = await fetchSatelliteInfo(name);
      setWikiInfo(info);
      setExpanded(!!info);
      if (!info) setWikiError(true);
    } catch {
      setWikiError(true);
      setExpanded(false);
    } finally {
      setWikiLoading(false);
    }
  }, [entity, wikiLoading]);

  if (!selectedEntityId || !entity || entity.type !== 'satellite') return null;

  const props = entity.properties as unknown as SatelliteProperties;
  const name = props?.name ?? entity.id;
  const noradId = props?.noradId;
  const constellation = props?.constellation;
  const orbitalAltKm = props?.orbitalAltKm ?? (entity.position.alt / 1000);
  const inclination = props?.inclination;
  const period = props?.period;

  const handleClose = () => { setSelectedEntity(null); stopSatelliteTracking(); };

  const popupClasses = [styles.popup, expanded ? styles.expanded : ''].filter(Boolean).join(' ');

  // Shared content for both desktop and mobile
  const satContent = (
    <>
      {trackedEntityId && (
        <div className={styles.trackingBadge}>
          <span className={styles.trackingDot} />
          TRACKING
        </div>
      )}

      <div className={styles.section}>
        <div className={styles.sectionTitle}>SATELLIT</div>
        <div className={styles.grid}>
          {noradId != null && <Row label="NORAD" value={`${noradId}`} />}
          {constellation && <Row label="KONST" value={constellation} />}
          <Row label="ORBIT" value={altClass(orbitalAltKm)} />
          <Row label="SRC" value="CELESTRAK" />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>POSITION</div>
        <div className={styles.grid}>
          <Row label="LAT" value={fmtCoord(entity.position.lat)} suffix={entity.position.lat >= 0 ? '° N' : '° S'} />
          <Row label="LON" value={fmtCoord(entity.position.lon)} suffix={entity.position.lon >= 0 ? '° E' : '° W'} />
          <Row label="ALT" value={fmtAltKm(entity.position.alt)} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>ORBIT-PARAMETER</div>
        <div className={styles.grid}>
          {inclination != null && <Row label="INC" value={`${inclination}°`} />}
          {period != null && period > 0 && <Row label="PERIOD" value={fmtPeriod(period)} />}
          <Row label="HÖHE" value={`${Math.round(orbitalAltKm)} km`} />
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionTitle}>STATUS</div>
        <div className={styles.grid}>
          <Row label="UPD" value={timeAgo(entity.timestamp)} />
        </div>
      </div>

      {!expanded && !wikiError && (
        <button
          className={styles.moreInfoBtn}
          onClick={handleMoreInfo}
          disabled={wikiLoading}
          id="satellite-more-info-btn"
        >
          {wikiLoading ? (
            <><span className={styles.loadingSpinner} />LADEN…</>
          ) : (
            '↗ MEHR INFO'
          )}
        </button>
      )}

      {wikiError && !wikiInfo && (
        <a
          className={styles.moreInfoBtn}
          href={noradId != null ? `https://n2yo.com/?s=${noradId}` : `https://duckduckgo.com/?q=${encodeURIComponent(name + ' satellite')}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'none' }}
        >
          {noradId != null ? '↗ AUF N2YO ÖFFNEN' : '🔍 IM WEB SUCHEN'}
        </a>
      )}

      {expanded && wikiInfo && (
        <div className={styles.wikiSection}>
          <div className={styles.sectionTitle}>WIKIPEDIA</div>
          {wikiInfo.thumbnailUrl && (
            <img className={styles.wikiImage} src={wikiInfo.thumbnailUrl} alt={wikiInfo.title} loading="lazy" />
          )}
          <p className={styles.wikiExtract}>{wikiInfo.extract}</p>
          <a
            className={styles.wikiLink}
            href={wikiInfo.articleUrl}
            target="_blank"
            rel="noopener noreferrer"
            id="satellite-wiki-link"
          >
            📖 ARTIKEL AUF WIKIPEDIA ÖFFNEN
          </a>
        </div>
      )}
    </>
  );

  // Desktop popup
  const desktopPopup = (
    <div
      className={popupClasses}
      style={dragStyle}
      id="satellite-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.callsign}>🛰️ {name}</span>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} title="Schliessen (ESC)" id="satellite-popup-close">✕</button>
      </div>
      {satContent}
    </div>
  );

  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={name}
        entitySub={noradId != null ? `NORAD ${noradId}` : altClass(orbitalAltKm)}
        icon="🛰️"
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        {satContent}
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value}{suffix && <span className={styles.suffix}>{suffix}</span>}
      </span>
    </div>
  );
}
